import uuid from 'uuid-random';
import type { PartnerService } from '.';
import type {
  Partner,
  UpdateVolumeEstimatesParams,
  UpdateReportingConfigParams,
  OptIns,
  PartnerReportingConfig,
  ReturnServiceMethod,
  UserSsoMutate,
  UserFetchParams,
  PartnerPackagingV2,
  PackagingSetV2,
  UserSsoFetchParams,
} from './types';
import type { User, ChangePasswordRequest } from '../AuthService/types';
import { mockUser, mockPartner, mockPackagingV2, mockPackagingSet } from './fixtures';
import delay from '../mockServiceDelay';

export const mockPartnerService: PartnerService = {
  updatePartner: async (partnerRequest: any) => {
    await delay();
    const partner: Partner = {
      ...partnerRequest,
      partnerId: uuid(),
      featureToggles: {},
      name: partnerRequest.name,
      type: partnerRequest.type,
    };
    return partner;
  },
  getPartnerUsers: async (partnerId: string, params: UserFetchParams | UserSsoFetchParams = {}) => {
    await delay();
    return {
      data: [mockUser],
      pageNext: '1',
      pagePrevious: '0',
      totalCount: 1,
    };
  },
  createPartnerUser: async (createUserRequest: any, accessToken: string) => {
    await delay();
    const user: User = {
      userId: uuid(),
      ...createUserRequest,
      active: true,
    };
    return user;
  },
  changePassword: async (changePasswordRequest: ChangePasswordRequest) => {
    await delay();
    return;
  },
  updateVolumeEstimates: async (args: UpdateVolumeEstimatesParams): Promise<Partner> => {
    await delay();
    mockPartner.pageViewEstimates = args.volumeEstimates;
    return Promise.resolve(mockPartner);
  },
  upsertReturnServiceMethod: async (partnerId: string, payload: ReturnServiceMethod) => {
    await delay();
    return Promise.resolve({ ...mockPartner, returnsServiceMethods: [payload] });
  },
  updatePartnerConfig: async (partner: Partner): Promise<Partner> => {
    await delay();
    return Promise.resolve(partner);
  },
  updateReportingConfig: async (args: UpdateReportingConfigParams): Promise<PartnerReportingConfig> => {
    await delay();
    const reportingConfigurationInfo = {
      timeZoneId: args.reportingZone,
      startingDayOfWeek: args.startDayOfWeek,
      orderToDeliverSlaDays: args.orderToDeliverSlaDays,
      orderToShipSlaDays: args.orderToShipSlaDays,
    };
    return Promise.resolve(reportingConfigurationInfo);
  },
  fetchPartner: async (partnerId: string): Promise<Partner> => {
    await delay();
    return Promise.resolve(mockPartner);
  },
  verifyEmailConfirmationToken: async (token: string) => {
    await delay();
    if (token === 'fail-token') {
      throw new Error('Unable to verify email confirmation token');
    }
    return Promise.resolve();
  },
  updatePartnerOptIns: async (optIns: OptIns) => {
    await delay();
    return { ...mockPartner, optIns };
  },
  fetchPartnerPackagingV2: async () => {
    await delay();
    return [...mockPackagingV2];
  },
  fetchPackagingSets: async () => {
    await delay();
    return [...mockPackagingSet];
  },

  createPartnerPackagingV2: async (packaging: PartnerPackagingV2) => {
    await delay();
    packaging.packagingId = uuid();
    mockPackagingV2.push(packaging);
    return packaging;
  },

  createPackagingSet: async (packagingSet: PackagingSetV2) => {
    await delay();
    packagingSet.packagingIds = [uuid()];
    mockPackagingSet.push(packagingSet);
    return packagingSet;
  },

  updatePackagingSet: async (packagingSetId: string, packagingSet: PackagingSetV2) => {
    await delay();
    packagingSet.packagingIds = [uuid()];
    mockPackagingSet.push(packagingSet);
    return packagingSet;
  },

  addPackagingToSet: async (packagingSetId: string) => {
    return mockPackagingSet[0];
  },

  deletePackagingFromSet: async (packagingSetId: string) => {
    return mockPackagingSet[0];
  },
  updatePartnerPackagingV2: async (packaging: PartnerPackagingV2) => {
    await delay();
    const packageToUpdate = mockPackagingV2.splice(
      mockPackagingV2.findIndex((pkg) => pkg.packagingId === packaging.packagingId),
      1,
    );
    const updatedPackage = { ...(packageToUpdate[0] || {}), ...packaging };
    mockPackagingV2.push(updatedPackage);
    return updatedPackage;
  },
  updatePartnerPasswordSettings: async (partnerId: string, payload: any) => {
    await delay();
    return { ...mockPartner, securityConfigurationInfo: { passwordConfigurationInfo: payload } };
  },
  fetchV1AssetHistory: async (partnerId: string) => {
    return [
      {
        creationTimeStamp: '2021-07-22T15:48:27.209Z',
        lastUpdatedTimeStamp: '2021-07-23T20:37:34.178Z',
        version: 3,
        currentDocument: {
          partnerId: 'parnterId:123',
          configurationDetails: {
            isAGreatPartner: false,
            usesLimits: true,
          },
        },
        previousDocument: {
          partnerId: 'parnterId:123',
          configurationDetails: {
            isAGreatPartner: true,
            usesLimits: false,
          },
        },
        assetType: 'partner',
        assetId: '222c3d93-ca51-4319-8ae1-5c354d5b3ad4',
        urn: '27ab9ede-5593-4e1a-af74-02b8d9128f62:86ad0286-d455-4c6f-b2ea-04f98f623543:1:222c3d93-ca51-4319-8ae1-5c354d5b3ad4:1',
        name: 'just great cables',
      },
      {
        creationTimeStamp: '2021-07-22T15:48:27.209Z',
        lastUpdatedTimeStamp: '2021-07-22T17:48:27.209Z',
        version: 2,
        currentDocument: {
          partnerId: 'parnterId:123',
          configurationDetails: {
            isAGreatPartner: true,
            usesLimits: false,
          },
        },
        previousDocument: {
          partnerId: 'parnterId:123',
        },
        assetType: 'partner',
        assetId: '222c3d93-ca51-4319-8ae1-5c354d5b3ad4',
        urn: '27ab9ede-5593-4e1a-af74-02b8d9128f62:86ad0286-d455-4c6f-b2ea-04f98f623543:1:cbf08562-6a6d-47c0-9074-1414d7d92a1d:1',
        name: 'just great cables',
      },
    ];
  },
  deleteSsoUser: function (userId: string): Promise<any> {
    throw new Error('Function not implemented.');
  },
  addSsoUser: function (newUser: UserSsoMutate): Promise<any> {
    throw new Error('Function not implemented.');
  },
  updateSsoUser: function (userId: string, user: Partial<UserSsoMutate>): Promise<any> {
    throw new Error('Function not implemented.');
  },
  getPartnerSsoUsers: function (partnerId: string, params?: UserFetchParams): Promise<any> {
    throw new Error('Function not implemented.');
  },
  getPartnerUser: async (userId: string) => {
    throw new Error('Function not implemented.');
  },
};

export default mockPartnerService;
